import { atomWithStorage } from "jotai/utils";

import { Basket, RoamingInfo, SubscriberPackage } from "@/services/model";
import { readStorageOnInit } from "@/utils/atoms";
import { getPreferredIncludedRoamingPackage } from "@/utils/deviceUtils";

export const optionBasketAtom = atomWithStorage<Basket | undefined>(
  "optionBasket",
  undefined,
  undefined,
  readStorageOnInit,
);

/** A roaming package with a 12-month contract is billed as one yearly package. */
export const isYearlyRoamingPackage = (
  minContractDur?: SubscriberPackage["minContractDur"],
  minContractDurUnit?: SubscriberPackage["minContractDurUnit"],
) => minContractDurUnit === "months" && minContractDur === 12;

type PartitionedRoamingOptions = {
  /** The included Flat Mobile package shown on the mobile subscription card, if any. */
  mobileAboPackage?: RoamingInfo;
  /**
   * Booked travel packages plus any surplus `includedInService` packages that
   * did not match the product-matrix `roamingProduct` for the SIM's tariff.
   * Surplus included packages are normalized with `includedInService: false`
   * so travel-package UI treats them as regular booked packages.
   */
  travelPackages: RoamingInfo[];
};

/**
 * Splits roaming options into the mobile subscription's included package and travel
 * packages. When the backend returns more than one `includedInService: true`
 * entry, the one whose `roamingProduct` matches the tariff's product-matrix
 * expectation is preferred; remaining included entries fall back to travel
 * packages. If none match, the first included entry is used as the mobile subscription's.
 */
export const partitionRoamingOptionsForDisplay = (
  roamingOptions: RoamingInfo[],
  serviceId?: number,
): PartitionedRoamingOptions => {
  const preferred = getPreferredIncludedRoamingPackage(
    roamingOptions,
    serviceId,
  );
  const notIncluded = roamingOptions.filter(
    (option) => !option.includedInService,
  );

  if (!preferred) {
    return { travelPackages: notIncluded };
  }

  const surplusIncluded = roamingOptions
    .filter((option) => option.includedInService && option !== preferred)
    .map((option) => ({ ...option, includedInService: false }));

  return {
    mobileAboPackage: preferred,
    travelPackages: [...notIncluded, ...surplusIncluded],
  };
};
